import type { z } from 'zod';

import { getAppConfig } from '@/app/config';

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }

  /** Sesja wygasła albo token został cofnięty — trzeba przelogować. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Zalogowany, ale poza zakresem dostępu (np. cudza parafia). */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * Tokeny żyją w react-oidc-context i w store sesji tokenowej, a nie w module.
 * SessionProvider podstawia tu gettery, żeby warstwa fetch nie musiała
 * znać Reacta ani przeciągać tokenów przez propsy.
 *
 * Dwa niezależne slot-y (Bearer vs X-Access-Token) zamiast jednego wspólnego,
 * bo tego wymaga backend: „w zależności od tego, jak użytkownik został
 * zalogowany”. Nigdy nie wysyłamy obu naraz — patrz `request()` poniżej.
 */
type TokenGetter = () => string | undefined;

let readAccessToken: TokenGetter = () => undefined;
let readCredentialToken: TokenGetter = () => undefined;

export function setAccessTokenGetter(getter: TokenGetter): void {
  readAccessToken = getter;
}

export function setCredentialTokenGetter(getter: TokenGetter): void {
  readCredentialToken = getter;
}

export type ResolvedAuthHeader =
  | { name: 'Authorization'; value: `Bearer ${string}`; via: 'oidc' }
  | { name: 'X-Access-Token'; value: string; via: 'token' }
  | null;

/**
 * Dobór autoryzacji zależy od tego, jak użytkownik się zalogował:
 *  – OIDC (Keycloak) → Authorization: Bearer <access_token>,
 *  – token → X-Access-Token: <raw token>.
 * Bearer ma pierwszeństwo, bo jeśli obie sesje żyją równolegle (np. użytkownik
 * otworzył kartę z aktywnym Keycloakiem, a wcześniej wpisał token), tożsamość
 * z Keycloaka jest silniejsza.
 *
 * Eksportowana (nie tylko wewnętrzna), żeby panel diagnostyczny mógł pokazać,
 * który nagłówek faktycznie poleci — bez duplikowania tej reguły w drugim miejscu.
 */
export function resolveAuthHeader(): ResolvedAuthHeader {
  const accessToken = readAccessToken();
  if (accessToken) {
    return { name: 'Authorization', value: `Bearer ${accessToken}`, via: 'oidc' };
  }

  const credentialToken = readCredentialToken();
  if (credentialToken) {
    return { name: 'X-Access-Token', value: credentialToken, via: 'token' };
  }

  return null;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** `FormData` (np. upload pliku) leci bez zmian; wszystko inne jako JSON. */
  body?: unknown;
  signal?: AbortSignal;
}

async function request(path: string, options: RequestOptions = {}): Promise<unknown> {
  const { dashboardApiUrl } = getAppConfig();
  const headers = new Headers({ Accept: 'application/json' });

  const auth = resolveAuthHeader();
  if (auth) {
    headers.set(auth.name, auth.value);
  }

  const isFormData = options.body instanceof FormData;
  // Content-Type dla FormData ustawia sam fetch — musi zawierać boundary
  // wygenerowany przy serializacji, którego tu nie znamy.
  if (options.body !== undefined && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }

  let response: Response;
  try {
    response = await fetch(`${dashboardApiUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      mode: 'cors',
      body:
        options.body === undefined
          ? undefined
          : isFormData
            ? (options.body as FormData)
            : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // `fetch()` odrzucone (TypeError: Failed to fetch) — brak serwera, CORS
    // albo zerwane połączenie w trakcie. Bez tego surowy komunikat przeglądarki
    // leciałby wprost do UI (patrz ten sam wzorzec w features/auth/api.ts).
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    const detail = error instanceof Error ? error.message : String(error);
    throw new ApiError(0, `Nie udało się połączyć z serwerem (${detail}).`);
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readErrorMessage(response));
  }

  if (response.status === 204) return null;
  return response.json();
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const payload: unknown = await response.json();
    if (
      typeof payload === 'object' &&
      payload !== null &&
      'message' in payload &&
      typeof payload.message === 'string' &&
      // Spring Boot na 404/500 domyślnie zwraca { message: "" } (puste ze
      // względów bezpieczeństwa, chyba że server.error.include-message jest
      // włączone) — pusty string przechodziłby tu bez tej blokady i InlineAlert
      // pokazywałby całkiem pustą treść zamiast któregoś z fallbacków niżej.
      payload.message.trim().length > 0
    ) {
      return payload.message;
    }
  } catch {
    // Odpowiedź bez JSON-a — zostajemy przy komunikacie ogólnym.
  }

  if (response.status === 401) return 'Sesja wygasła. Zaloguj się ponownie.';
  if (response.status === 403) return 'Brak uprawnień do tych danych.';
  if (response.status >= 500) return 'Błąd serwera. Spróbuj ponownie za chwilę.';
  return `Żądanie nie powiodło się (HTTP ${String(response.status)}).`;
}

/**
 * Żądanie z walidacją odpowiedzi schematem Zoda.
 * Docelowo schematy zastąpi generowanie typów z OpenAPI (openapi-typescript),
 * ale kontrakt runtime zostaje — chroni przed cichym rozjazdem front/back.
 */
export async function apiFetch<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  options?: RequestOptions,
): Promise<z.infer<TSchema>> {
  const payload = await request(path, options);
  const parsed = schema.safeParse(payload);

  if (!parsed.success) {
    throw new ApiError(502, `Odpowiedź API dla ${path} ma nieoczekiwany kształt.`);
  }

  return parsed.data;
}
